import { Component, inject, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersService } from '../users.service';
import { User, CreateUserRequest, UpdateUserRequest } from '../users.type';

interface DialogData {
  mode: 'create' | 'edit';
  user?: User;
}

@Component({
  selector: 'app-user-form-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule
  ],
  templateUrl: './user-form-dialog.html',
  styleUrl: './user-form-dialog.scss'
})
export class UserFormDialog {
  private fb = inject(FormBuilder);
  private usersService = inject(UsersService);
  private snackBar = inject(MatSnackBar);
  private dialogRef = inject(MatDialogRef<UserFormDialog>);

  userForm: FormGroup;
  isEditMode: boolean;
  submitting: boolean = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogData) {
    this.isEditMode = data.mode === 'edit';

    this.userForm = this.fb.group({
      username: [data.user?.username || '', Validators.required],
      password: ['', this.isEditMode ? [] : Validators.required],
      // 'role' field only used on create; hidden in edit mode
      role: [this.isEditMode ? '' : 'Admin', this.isEditMode ? [] : Validators.required]
    });
  }

  onSubmit(): void {
    if (this.userForm.invalid) {
      return;
    }

    this.submitting = true;

    if (this.isEditMode) {
      this.updateUser();
    } else {
      this.createUser();
    }
  }

  private createUser(): void {
    const { username, password, role } = this.userForm.value;
    const request: CreateUserRequest = { username, password, role };

    this.usersService.createUser(request).subscribe({
      next: () => {
        this.snackBar.open('User created successfully', 'Close', { duration: 5000 });
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Failed to create user:', error);
        this.snackBar.open('Error creating user', 'Close', { duration: 5000 });
        this.submitting = false;
      }
    });
  }

  private updateUser(): void {
    const { username, password } = this.userForm.value;
    const request: UpdateUserRequest = { username };
    if (password) {
      request.password = password;
    }

    this.usersService.updateUser(this.data.user!.user_id, request).subscribe({
      next: () => {
        this.snackBar.open('User updated successfully', 'Close', { duration: 5000 });
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Failed to update user:', error);
        this.snackBar.open('Error updating user', 'Close', { duration: 5000 });
        this.submitting = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
